import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

/**
 * The owner works across several clients, so every request carries which one
 * they're currently in. The backend ignores this header for client staff and
 * admins — they're pinned to their own business server-side.
 */
export function setTenantHeader(tenantId) {
  if (tenantId) api.defaults.headers.common["X-Tenant-Id"] = tenantId;
  else delete api.defaults.headers.common["X-Tenant-Id"];
}

/**
 * The server returns 401 when a session is no longer usable — signed out,
 * expired, or the client business was suspended or deleted. Rather than each
 * screen showing an error the person can ignore, one handler signs them out.
 */
let onUnauthorized = null;

export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Failing to sign in is a normal 401 and must not trigger a sign-out loop.
    const isAuthAttempt = url.includes("/auth/login") || url.includes("/auth/register");
    if (status === 401 && !isAuthAttempt && onUnauthorized) {
      const detail = err?.response?.data?.detail;
      onUnauthorized(typeof detail === "string" ? detail : "");
    }
    return Promise.reject(err);
  }
);

/** 428 means "you haven't chosen a client yet" — the app redirects on it. */
export const NEEDS_TENANT = 428;

export function needsTenant(err) {
  return err?.response?.status === NEEDS_TENANT;
}

/** Turn a FastAPI error payload into one sentence a person can act on. */
export function apiError(err) {
  const detail = err?.response?.data?.detail ?? err?.message;
  if (detail == null) return "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

// created_at (and similar) come back as a full ISO timestamp — unlike
// formatDate's date-only fields, this is parsed as-is (not re-anchored to
// local midnight) so the time-of-day is real, not a placeholder.
export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} at ${d.toLocaleTimeString(
    undefined,
    { hour: "numeric", minute: "2-digit" }
  )}`;
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/** Fetches a file from the API (with the session) and saves it. */
export async function downloadFile(path, fallbackName, params) {
  const res = await api.get(path, { responseType: "blob", params });
  const cd = res.headers?.["content-disposition"] || "";
  const m = cd.match(/filename="?([^";]+)"?/);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(res.data);
  link.download = m ? m[1] : fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

/** "12 Dec 2025" style, or "" — for places where a dash would be noise. */
export function shortDate(value) {
  if (!value) return "";
  return formatDate(value);
}

export function plural(n, word, many) {
  return `${n} ${n === 1 ? word : many || word + "s"}`;
}
